import { ChevronDownIcon } from 'lucide-react'
import { useState } from 'react'

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui'
import type { AllAccountsAsOf } from '../model/portfolioOverview'
import { VerdictBadge } from '../VerdictBadge'
import { AccountCount } from './AccountCount'
import { AccountMenu } from './AccountMenu'

interface ShowingSelectorProps {
  asOf: AllAccountsAsOf
  selectedAccountId: string | null
  onSelectAccount: (accountId: string | null) => void
}

// The start of the lower half: "Showing All accounts" or one account, with that account's verdict.
export const ShowingSelector = ({
  asOf,
  selectedAccountId,
  onSelectAccount,
}: ShowingSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const chosen = asOf.accounts.find(
    (account) => account.accountId === selectedAccountId,
  )
  const choose = (accountId: string | null) => {
    onSelectAccount(accountId)
    setIsOpen(false)
  }
  const [firstReason] = chosen?.badge.reasons ?? []

  return (
    <div className="mt-5 flex min-h-10 flex-wrap items-center justify-between gap-4 text-blue-7">
      <div className="flex min-w-0 items-center gap-3">
        <span className="text-sm text-ink-subtle">Showing</span>
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="flex h-10 min-w-[340px] cursor-pointer items-center justify-between gap-4 whitespace-nowrap rounded-md border bg-white pr-3 pl-3.5 text-base font-semibold data-[state=open]:border-blue-4 data-[state=open]:ring-3 data-[state=open]:ring-blue-2"
            >
              <span className="flex items-center gap-2">
                {chosen ? (
                  chosen.name
                ) : (
                  <>
                    All accounts
                    <AccountCount count={asOf.accounts.length} />
                  </>
                )}
              </span>
              <ChevronDownIcon className="size-4 text-blue-5" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[560px] p-1.5">
            <AccountMenu
              accounts={asOf.accounts}
              selectedAccountId={selectedAccountId}
              onChoose={choose}
            />
          </PopoverContent>
        </Popover>
        {chosen && (
          <span className="text-sm text-ink-subtle">{chosen.accountType}</span>
        )}
      </div>
      {chosen && chosen.status !== 'before_first_statement' && (
        <VerdictBadge
          status={chosen.badge.status}
          reason={firstReason}
          moreReasons={Math.max(chosen.badge.reasons.length - 1, 0)}
        />
      )}
    </div>
  )
}
